/**
 * Query hooks for Settings
 * 
 * These hooks fetch application configuration and settings
 * with long cache times since they rarely change.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query/client';
import { BACKEND_URL } from "../../config";
/**
 * Fetch slider settings (used for "nearby" radius, etc.)
 */
export function useSliderSettings() {
  return useQuery({
    queryKey: queryKeys.settings.sliders(),
    queryFn: async () => {
      const res = await api.get("/api/settings/sliders");
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - settings rarely change
    gcTime: 30 * 60 * 1000,   // 30 minutes cache
  });
}

/**
 * Fetch public config
 */
export function usePublicConfig() {
  return useQuery({
    queryKey: queryKeys.settings.config(),
    queryFn: async () => {
      const res = await api.get("/api/config/public");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch export settings for current user
 */
export function useExportSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.settings.export(),
    queryFn: async () => {
      const res = await api.get("/api/settings/export");
      const tripEnabled = res.data.trip_export_enabled ?? res.data.own_export_enabled ?? res.data.export_enabled ?? true;
      return {
        tripExportEnabled: tripEnabled,
        gdprExportEnabled: res.data.gdpr_export_enabled ?? res.data.total_export_enabled ?? true,
        // Legacy compat
        ownExportEnabled: tripEnabled,
        othersExportEnabled: tripEnabled,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
